/**
 * KolAskPage — קוֹל (Voice) Thread. The car answers first-person, calm, and
 * every fact carries a source chip (the trust contract). Voice is first-class
 * (push-to-talk dictation + live transcription + opt-in TTS readback) but the
 * text path is always available — never voice-only. Long answers split into
 * separate bubbles. Mounted at /kol/vehicles/:id/ask.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, askVehicle, getVehicle, type AskCitation, type VehicleSummary } from '@/lib/api';
import { Orb, type OrbState } from '@/components/kol/Orb';
import { SourceChip } from '@/components/kol/SourceChip';
import { SuggestionChip } from '@/components/kol/SuggestionChip';
import { Icon } from '@/components/kol/Icon';
import { docTypeIconName } from '@/lib/kol/docs';
import { useDictation } from '@/hooks/useDictation';

const PINE_GRAD = 'linear-gradient(155deg, #2E7D5B, #1F4E3D)';
const CLAY_GRAD = 'linear-gradient(155deg, #B5713F, #8A4F28)';
import { cancelSpeech, isTtsSupported, speak } from '@/lib/kol/tts';

interface Turn {
  id: string;
  role: 'you' | 'car';
  text: string;
  citations?: AskCitation[];
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Split a long answer into calm, separate bubbles (mobile multitasking). */
function splitAnswer(text: string): string[] {
  const paras = text
    .split(/\n\s*\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return paras.length ? paras : [text.trim()];
}

function stripDocMarkers(text: string): string {
  return text
    .replace(/\[doc:[a-zA-Z0-9_-]+\]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Map a document type to a localized label via i18n (en/ar users must not see
// Hebrew source-chip labels). Reuses the existing doc_types.* keys.
function prettyDocType(type: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const key = type.toLowerCase();
  const known = [
    'vehicle_registration',
    'driver_license',
    'insurance_compulsory',
    'insurance_comprehensive',
    'service_contract',
    'insurance_policy',
  ];
  if (known.includes(key)) return t(`doc_types.${key}`);
  return t('doc_types.other');
}

/** Render answer text, turning inline [doc:id] markers into [n] badges. */
function renderWithCitations(text: string, citations: AskCitation[]): ReactNode {
  if (!citations.length) return stripDocMarkers(text);
  const idToIdx = new Map(citations.map((c, i) => [c.documentId, i + 1]));
  const parts: ReactNode[] = [];
  const regex = /\[doc:([a-zA-Z0-9_-]+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = regex.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const n = m[1] ? idToIdx.get(m[1]) : undefined;
    parts.push(
      n != null ? (
        <sup key={key++} className="text-kol-clay-ink font-bold">
          [{n}]
        </sup>
      ) : (
        ''
      ),
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function KolAskPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: vehicleId } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const [vehicle, setVehicle] = useState<VehicleSummary | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ttsOn, setTtsOn] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prefilled = useRef(false);

  const dictation = useDictation({
    onFinal: (text) => setQuestion((prev) => (prev ? `${prev} ${text}` : text)),
  });

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    getVehicle(vehicleId)
      .then((v) => !cancelled && setVehicle(v))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length, pending]);

  async function send(text: string) {
    const q = text.trim();
    if (!q || pending || !vehicleId) return;
    if (dictation.listening) dictation.stop();
    setError(null);
    setQuestion('');
    setTurns((prev) => [...prev, { id: `u-${Date.now()}`, role: 'you', text: q }]);
    setPending(true);
    try {
      const res = await askVehicle(vehicleId, q);
      const bubbles = splitAnswer(res.answer);
      const base = Date.now();
      setTurns((prev) => [
        ...prev,
        ...bubbles.map((b, i) => ({
          id: `c-${base}-${i}`,
          role: 'car' as const,
          text: b,
          citations: i === bubbles.length - 1 ? res.citations : undefined,
        })),
      ]);
      if (ttsOn) speak(stripDocMarkers(res.answer));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error', { defaultValue: 'משהו השתבש' }));
    } finally {
      setPending(false);
    }
  }

  // Prefill + auto-send from a ?q= chip (once).
  useEffect(() => {
    const q = params.get('q');
    if (q && !prefilled.current && vehicleId) {
      prefilled.current = true;
      void send(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, vehicleId]);

  function toggleTts() {
    setTtsOn((on) => {
      if (on) cancelSpeech();
      return !on;
    });
  }

  function toggleMic() {
    if (!dictation.supported) {
      setError(t('kol.ask.no_voice', { defaultValue: 'הכתבה קולית אינה נתמכת בדפדפן זה. אפשר להקליד.' }));
      return;
    }
    if (dictation.listening) dictation.stop();
    else dictation.start();
  }

  const carName = vehicle?.customLabel?.trim() || (vehicle ? `${cap(vehicle.oem)} ${cap(vehicle.model)}` : '…');
  const orbState: OrbState = dictation.listening ? 'listening' : pending ? 'thinking' : 'idle';

  return (
    <div className="kol-grain relative font-kol min-h-screen bg-kol-canvas text-kol-ink flex flex-col" dir={i18n.dir()}>
      <header className="relative z-[1] flex items-center justify-between px-5 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-kol-line-soft">
        <div className="flex items-center gap-3 min-w-0">
          <Orb state={orbState} size={40} />
          <div className="min-w-0">
            <div className="text-[18px] font-bold truncate">{carName}</div>
            <div className="text-[12px] text-kol-ink-faint">
              {dictation.listening
                ? t('kol.ask.listening', { defaultValue: 'מקשיב…' })
                : pending
                  ? t('kol.ask.thinking', { defaultValue: 'חושב…' })
                  : t('kol.ask.subtitle', { defaultValue: 'שיחה' })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isTtsSupported() && (
            <button
              type="button"
              onClick={toggleTts}
              aria-pressed={ttsOn}
              aria-label={t('kol.ask.tts', { defaultValue: 'הקראה קולית' })}
              className={`w-11 h-11 grid place-items-center text-[18px] rounded-full ${ttsOn ? 'text-kol-sage-deep bg-kol-sage-soft' : 'text-kol-ink-faint'}`}
            >
              {ttsOn ? '🔊' : '🔇'}
            </button>
          )}
          <button
            type="button"
            aria-label={t('common.close', { defaultValue: 'סגירה' })}
            onClick={() => navigate(vehicleId ? `/kol/vehicles/${vehicleId}` : '/kol')}
            className="w-11 h-11 rounded-full border border-kol-line bg-kol-card-warm grid place-items-center text-kol-ink-soft shadow-kol-1"
          >
            <Icon name="close" size={19} />
          </button>
        </div>
      </header>

      <div ref={scrollRef} className="relative z-[1] flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3.5">
        {turns.length === 0 && !pending && (
          <div className="pt-6 flex flex-col items-center gap-5 text-center">
            <Orb state="idle" size={96} />
            <p className="text-[17px] text-kol-ink-soft leading-relaxed max-w-[290px]">
              {t('kol.ask.empty', {
                defaultValue:
                  'שאלו על נורת אזהרה שנדלקה, לחץ אוויר, או כל דבר שכתוב במסמכים. אני עונה רק ממה שיש לי, ותמיד מראה מקור.',
              })}
            </p>
            <div className="w-full flex flex-col gap-2.5">
              <SuggestionChip
                icon={<Icon name="warning" size={18} />}
                onClick={() => send(t('kol.ask.q_warning', { defaultValue: 'מה אומרת נורת האזהרה שנדלקה?' }))}
              >
                {t('kol.ask.q_warning', { defaultValue: 'מה אומרת נורת האזהרה שנדלקה?' })}
              </SuggestionChip>
              <SuggestionChip
                icon={<Icon name="compass" size={18} />}
                onClick={() => send(t('kol.ask.q_tires', { defaultValue: 'מה לחץ האוויר בצמיגים?' }))}
              >
                {t('kol.ask.q_tires', { defaultValue: 'מה לחץ האוויר בצמיגים?' })}
              </SuggestionChip>
            </div>
          </div>
        )}

        {turns.map((turn) =>
          turn.role === 'you' ? (
            <div key={turn.id} className="self-start max-w-[84%] animate-kol-rise">
              <div
                className="rounded-[20px] rounded-br-md px-4 py-3 text-[16.5px] leading-relaxed text-white font-medium"
                style={{ background: PINE_GRAD }}
              >
                {turn.text}
              </div>
            </div>
          ) : (
            <div key={turn.id} className="self-end max-w-[88%] animate-kol-rise">
              <div className="rounded-[20px] rounded-bl-md px-4 py-3 bg-kol-card border border-kol-line-soft text-[16.5px] leading-relaxed shadow-kol-1">
                {renderWithCitations(turn.text, turn.citations ?? [])}
                {turn.citations && turn.citations.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {turn.citations.map((c, idx) => (
                      <SourceChip key={c.documentId} onClick={() => navigate(`/documents/${c.documentId}?kol=1`)}>
                        <Icon name={docTypeIconName(c.documentType)} size={13} className="stroke-kol-clay-ink" />[
                        {idx + 1}] {prettyDocType(c.documentType, t)}
                      </SourceChip>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ),
        )}

        {pending && (
          <div className="self-end">
            <div className="flex gap-1.5 px-4 py-3.5 rounded-[20px] rounded-bl-md bg-kol-card border border-kol-line">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full bg-kol-sage animate-bounce"
                  style={{ animationDelay: `${i * 0.18}s`, animationDuration: '1.1s' }}
                />
              ))}
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="text-[14px] text-kol-clay text-center">
            {error}
          </p>
        )}
      </div>

      <div className="relative z-[1] px-5 pt-3 pb-[max(14px,env(safe-area-inset-bottom))]">
        {dictation.listening && (
          <div className="flex items-center justify-center gap-3 mb-2.5 text-kol-ink-faint text-[14px]">
            <span className="kol-wave" aria-hidden>
              <i></i>
              <i></i>
              <i></i>
              <i></i>
              <i></i>
              <i></i>
              <i></i>
            </span>
            <span>{dictation.interim || t('kol.ask.listening', { defaultValue: 'מקשיב…' })}</span>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send(question);
          }}
          className="kol-glass flex items-center gap-2.5 rounded-[22px] ps-[18px] p-2 shadow-kol-3"
        >
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('kol.ask.placeholder', { defaultValue: 'דברו או הקלידו…' })}
            disabled={pending}
            aria-label={t('kol.ask.placeholder', { defaultValue: 'דברו או הקלידו' })}
            className="flex-1 bg-transparent outline-none text-[16.5px] text-kol-ink placeholder:text-kol-ink-faint disabled:opacity-50 min-h-[44px]"
          />
          {question.trim() ? (
            <button
              type="submit"
              disabled={pending}
              aria-label={t('kol.ask.send', { defaultValue: 'שליחה' })}
              className="w-12 h-12 rounded-full grid place-items-center text-white text-[20px] shrink-0 disabled:opacity-40"
              style={{ background: PINE_GRAD }}
            >
              ↑
            </button>
          ) : (
            <button
              type="button"
              onClick={toggleMic}
              aria-label={t('kol.ask.mic', { defaultValue: 'דברו אל הרכב' })}
              aria-pressed={dictation.listening}
              className="w-12 h-12 rounded-full grid place-items-center text-white shrink-0"
              style={{
                background: dictation.listening ? CLAY_GRAD : PINE_GRAD,
                boxShadow: '0 8px 18px -6px rgba(31, 80, 66, 0.55)',
              }}
            >
              <Icon name="mic" size={21} strokeWidth={1.7} />
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
